import { useCallback, useEffect, useRef, useState } from 'react';

import { css } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';
import { useTabActiveContext } from '@/libraries/design/patterns/tabs/wac-refresh-tabs/src/tab-active-context';

import { PlayPauseControls } from '../../play-pause-controls/src/play-pause-controls';

export interface VideoProps {
	src: string;
	/**
	 * Optional companion source for cross-browser playback. To support
	 * transparent (alpha-channel) videos in every modern browser, supply BOTH:
	 *
	 *   - `src`         → a `.webm` encoded with VP9 + alpha (Chrome/Firefox/Edge)
	 *   - `fallbackSrc` → a `.mp4` (or `.mov`) encoded with HEVC (H.265 / hvc1)
	 *                     and alpha (Safari, iOS)
	 *
	 * The component always renders the HEVC source FIRST with
	 * `type='video/mp4; codecs="hvc1"'` (so Safari picks it) and the WebM
	 * SECOND (so Chrome/Firefox skip the HEVC and use the WebM with alpha).
	 * This matches the cross-browser pattern documented at
	 * https://supergeekery.com/blog/transparent-video-in-chrome-edge-firefox-and-safari-circa-2022
	 *
	 * IMPORTANT: A plain H.264 `.mp4` will NOT carry an alpha channel — Safari
	 * will play it but the transparency will be opaque/baked-in. The fallback
	 * MUST be HEVC for Safari transparency. Either container (`.mp4` or `.mov`)
	 * is fine; the codec inside is what matters.
	 */
	fallbackSrc?: string;
	alt?: string;
	poster?: string;
	roundedCorners?: boolean;
	desktopHeight?: string;
	tabletHeight?: string;
	mobileHeight?: string;
	disableAutoplay?: boolean;
	showVideoControls?: boolean;
	loop?: boolean;
	aspectRatio?: string;
	/**
	 * Adds a subtle neutral border around the video container. Useful for
	 * white/light videos placed on a white background so the video edges
	 * remain visible.
	 */
	border?: boolean;
}

const getVideoExtension = (src: string): string =>
	src.split('?')[0]?.split('#')[0]?.split('.').at(-1)?.toLowerCase() ?? '';

/**
 * Returns the `type` attribute for a single-source `<video>` (i.e. when no
 * companion `fallbackSrc` is provided). When a fallback IS provided, the
 * source list is hard-coded to `[HEVC mp4, webm]` and this helper is not used.
 */
const getSingleSourceType = (src: string): string => {
	const ext = getVideoExtension(src);
	if (ext === 'webm') return 'video/webm';
	return 'video/mp4';
};

export const Video = ({
	src,
	fallbackSrc,
	alt = '',
	poster,
	roundedCorners = false,
	desktopHeight,
	tabletHeight,
	mobileHeight,
	disableAutoplay = false,
	showVideoControls = false,
	loop = false,
	aspectRatio,
	border = false,
}: // eslint-disable-next-line complexity
VideoProps) => {
	const videoRef = useRef<HTMLVideoElement>(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const [hasError, setHasError] = useState(false);
	const isTabActive = useTabActiveContext();

	// Keep a ref so the IntersectionObserver callback can read the latest value
	// without needing to be re-created every time isTabActive changes.
	const isTabActiveRef = useRef(isTabActive);
	useEffect(() => {
		isTabActiveRef.current = isTabActive;
	}, [isTabActive]);

	// Track whether the tab has ever been inactive so the resume branch in the
	// tab-active effect does not fire on initial mount (when isTabActive is
	// already true and the IntersectionObserver hasn't run yet).
	const wasTabInactiveRef = useRef(false);

	// Reset the error state whenever the source list changes. This replaces the
	// old `onLoadStart={() => setHasError(false)}` handler, which fired on
	// every `<source>` the browser tried — racing with `onError` events from
	// earlier `<source>` elements (e.g. a codec rejection on the first source)
	// and intermittently leaving `hasError` stuck `true` between refreshes.
	useEffect(() => {
		setHasError(false);
	}, [src, fallbackSrc]);

	// Play from beginning when in viewport, pause when out.
	// Note: we intentionally do NOT depend on `hasError` here. A `<source>`-level
	// error must not prevent the play attempt — the browser will fall through to
	// the next source on its own. We still call `play()`; the returned promise
	// simply rejects if there's truly nothing to play, which we swallow.
	useEffect(() => {
		const video = videoRef.current;
		if (!video || disableAutoplay) return;

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry && entry.isIntersecting) {
					// Don't autoplay if the enclosing tab is not active.
					if (!isTabActiveRef.current) return;
					video.currentTime = 0; // Always start from beginning
					video.play().catch(() => {
						// Ignore play errors (autoplay restrictions, power
						// saving, no decodable source, etc.)
					});
				} else {
					video.pause();
				}
			},
			{ threshold: 0.5 },
		);

		observer.observe(video);

		return () => {
			observer.disconnect();
		};
	}, [disableAutoplay]);

	// When the enclosing tab becomes inactive, pause and reset the video so it
	// starts from the beginning when the tab is re-selected. When it becomes
	// active again, resume playback — the IntersectionObserver will not re-fire
	// because the element's intersection state hasn't changed (still in-viewport).
	useEffect(() => {
		const video = videoRef.current;
		if (!video || disableAutoplay) return;
		if (!isTabActive) {
			wasTabInactiveRef.current = true;
			video.pause();
			video.currentTime = 0;
			setIsPlaying(false);
		} else if (wasTabInactiveRef.current) {
			// Only resume when transitioning from inactive → active. Skips the
			// initial mount where isTabActive is already true (the
			// IntersectionObserver handles the first play in that case).
			// Guard with Promise.resolve() in case the environment's play()
			// does not return a Promise (e.g. jsdom in tests).
			Promise.resolve(video.play())
				.then(() => {
					setIsPlaying(true);
				})
				.catch(() => {
					// Ignore play errors (autoplay restrictions, power saving, etc.)
				});
		}
	}, [isTabActive, disableAutoplay]);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const togglePlay = useCallback(() => {
		if (videoRef.current && !hasError) {
			if (isPlaying) {
				videoRef.current.pause();
				setIsPlaying(false);
				createAnalyticsEvent({
					eventType: 'ui',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'pauseVideo',
					source: 'videoContainer',
					attributes: { label: 'pause' },
				}).fire();
			} else {
				videoRef.current
					.play()
					.then(() => {
						setIsPlaying(true);
						createAnalyticsEvent({
							eventType: 'ui',
							action: 'clicked',
							actionSubject: 'button',
							actionSubjectId: 'playVideo',
							source: 'videoContainer',
							attributes: { label: 'play' },
						}).fire();
					})
					.catch(() => {
						// Ignore error likely due to autoplay restrictions or browser power saving
						setIsPlaying(false);
					});
			}
		}
	}, [videoRef, hasError, isPlaying, createAnalyticsEvent]);

	const videoCss = css({
		width: '100%',
		[media.above.md]: {
			height: desktopHeight ? desktopHeight : 'auto',
		},
		[media.only.sm]: {
			height: tabletHeight ? tabletHeight : 'auto',
		},
		[media.below.sm]: {
			height: mobileHeight ? mobileHeight : 'auto',
		},
	});

	// When BOTH a primary and a fallback source are provided, the contract is:
	//   `src`         = .webm (VP9 + alpha)
	//   `fallbackSrc` = .mp4/.mov (HEVC / hvc1 + alpha)
	// The HEVC source MUST be rendered FIRST with `codecs="hvc1"` so Safari
	// picks it (Safari supports WebM but not WebM-with-alpha; without this
	// ordering Safari renders the alpha as a black background). Chrome/Firefox
	// do not support hvc1 and will skip past it to the WebM.
	// See: https://supergeekery.com/blog/transparent-video-in-chrome-edge-firefox-and-safari-circa-2022
	const sources: { src: string; type: string }[] = fallbackSrc
		? [
				{ src: fallbackSrc, type: 'video/mp4; codecs="hvc1"' },
				{ src, type: 'video/webm' },
			]
		: [{ src, type: getSingleSourceType(src) }];

	return (
		<div css={[css({ position: 'relative' }), roundedCorners && roundedCornersCss, border && borderCss]}>
			<video
				ref={videoRef}
				playsInline
				muted={!disableAutoplay && !showVideoControls}
				preload="auto"
				poster={poster}
				controls={showVideoControls}
				loop={loop}
				css={videoCss}
				style={{
					maxWidth: '100%',
					aspectRatio: aspectRatio,
				}}
				aria-label={alt}
				aria-hidden={alt === ''}
				onPlay={() => setIsPlaying(true)}
				onPause={() => setIsPlaying(false)}
				onEnded={() => setIsPlaying(false)}
				// Only mark the component as errored when the browser has
				// exhausted ALL `<source>` elements (NETWORK_NO_SOURCE). A
				// single source-level rejection (e.g. codec mismatch on the
				// HEVC fallback when the asset is actually H.264) is not a
				// failure — the browser will fall through to the next source.
				onError={() => {
					const v = videoRef.current;
					if (v && v.networkState === HTMLMediaElement.NETWORK_NO_SOURCE) {
						setHasError(true);
					}
				}}
			>
				{sources.map((source) => (
					<source key={source.src} src={source.src} type={source.type} />
				))}
				<track kind="captions" />
				Video not supported.
			</video>
			{!hasError && !showVideoControls && (
				<div css={controlContainerCss}>
					<PlayPauseControls
						isPlaying={isPlaying}
						onToggle={togglePlay}
						mediaLabel={alt !== '' ? alt : 'video'}
					/>
				</div>
			)}
		</div>
	);
};

const controlContainerCss = css({
	position: 'absolute',
	width: '100%',
	display: 'flex',
	justifyContent: 'end',
	insetBlockEnd: token('space.100'),
	padding: token('space.075'),
});

const roundedCornersCss = css({
	borderRadius: wacToken('corner.radius.card'),
	overflow: 'hidden',
});

const borderCss = css({
	border: `1px solid ${wacToken('color.neutral.light300')}`,
});
